import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { BaseService } from 'src/app/services/base.service';
import { OlxAdvertService } from '../../../../../services/olx-advert.service';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalService } from '../../../../../services/modal.service';
import { PlannerComponent } from '../../../../../modals/planner/planner.component';
import { ERROR_MESSAGES, activeTranslations, MODAL_TYPES } from '../../../../../enum/constants';
import moment from 'moment/moment';
import { IError } from '../../../../../interfaces';
import { NotificationService } from '../../../../../services/notifications.service';
import { DialogService } from '../../../../../services/dialog.service';

interface IFormGroup {
  currentPacket: FormControl;
  countAdverts: FormControl;
  zone: FormControl;
  currentZone: FormControl;
}

interface SelectedTariff {
  category_id: number;
  is_premium: boolean;
  name: string;
  package_id: string;
  price: number;
  size: number;
  type: string;
  variant_id: string;
  zone_id: string;
}

@Component({
  selector: 'app-buy-olx-package',
  templateUrl: './buy-olx-package.component.html',
  styleUrls: ['./buy-olx-package.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BuyOlxPackageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();
  formPacket!: FormGroup<IFormGroup>;
  public packet: [] = [];

  public categories: any[] = [];
  public current_category_id: number = 0;
  public packetsAll = [];
  public packets: any = {
    base: {},
    premium: {},
    mega: {},
  };
  private channel_id: any;
  selectedBase: any;
  selectedPremium: any;
  selectedMega: any;
  zones: any[] = [];
  currentZone: any;
  isMega: boolean = false;
  isPremium: boolean = false;
  isBase: boolean = false;
  isPacket: boolean = false;
  isCurrentZone: boolean = true;
  selectedTariff!: SelectedTariff;
  balance?: any;
  isLoader: boolean = false;
  calendar: any;
  planned_at_premium: any;
  planned_at_mega: any;
  planned_at_base: any;
  planned_at: any;
  isDraft?: string;
  notFound: boolean = false;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    readonly baseService: BaseService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.channel_id = this.payload?.channel_id;
    this.initOlxCategories();
    this.initForm();
    this.initBalance();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initOlxCategories() {
    this.olxAdvertService.getCategories(this.current_category_id).subscribe({
      next: (res) => {
        this.categories.push(res);
      },
      error: (err) => {},
    });
  }

  public initBalance() {
    this.olxAdvertService
      .getBalance(this.channel_id)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (balance) => {
          this.balance = balance;
        },
        error: () => {},
      });
  }

  public selectCategory(category: any, index: any) {
    if (index !== this.categories.length - 1) {
      this.categories.splice(index + 1);
      this.packets.premium.packages = [];
      this.packets.base.packages = [];
      this.packets.mega.packages = [];
      this.isPacket = false;
      this.current_category_id = category.category_id;
      this.notFound = false;
    } else {
      this.packets.premium.packages = [];
      this.packets.base.packages = [];
      this.packets.mega.packages = [];
    }
    this.current_category_id = category.category_id;

    if (category.is_leaf) {
      this.selectedBase = null;
      this.selectedPremium = null;
      this.selectedMega = null;
      this.planned_at_base = null;
      this.planned_at_premium = null;
      this.planned_at_mega = null;
      this.formPacket.controls.countAdverts.setValue(null);
      this.notFound = false;
      this.getZones();
    } else {
      this.formPacket.controls.countAdverts.setValue(null);
      this.selectedBase = null;
      this.selectedPremium = null;
      this.selectedMega = null;
      this.planned_at_base = null;
      this.planned_at_premium = null;
      this.planned_at_mega = null;

      this.initOlxCategories();
      this.zones = [];
      this.currentZone = null;
    }

    this.formPacket.controls['currentPacket'].setValue(null);
  }

  private initPackets(zone_id?: string) {
    this.isLoader = true;
    this.olxAdvertService.getPackets(this.current_category_id, this.channel_id, zone_id || '').subscribe({
      next: (res) => {
        if (!res.error) {
          this.notFound = false;
          this.packetsAll = res;
          this.isPacket = true;
          res.forEach((v: any) => {
            if (v.is_premium && v.type === 'base') {
              if (this.packets.premium?.packages?.length) {
                this.packets.premium.packages.push(v);
              } else {
                this.packets.premium.packages = [v];
              }
            }
            if (!v.is_premium && v.type === 'base') {
              if (this.packets.base?.packages?.length) {
                this.packets.base.packages.push(v);
              } else {
                this.packets.base.packages = [v];
              }
            }

            if (v.is_premium && v.type === 'mega') {
              if (this.packets.mega?.packages?.length) {
                this.packets.mega.packages.push(v);
              } else {
                this.packets.mega.packages = [v];
              }
            }
          });
          const p = Object.keys(this.packets.mega).length;
          if (p) {
            this.isPremium = true;
            const a = this.packets.premium.packages.sort((a: any, b: any) => a.size - b.size);
            this.packets.premium.packages = [...a];
          }

          const b = Object.keys(this.packets.mega).length;
          if (b) {
            this.isBase = true;
            const a = this.packets.base.packages.sort((a: any, b: any) => a.size - b.size);
            this.packets.base.packages = [...a];
          }

          const m = Object.keys(this.packets.mega).length;
          if (m) {
            this.isMega = true;
            const a = this.packets.mega.packages.sort((a: any, b: any) => a.size - b.size);
            this.packets.mega.packages = [...a];
          }
          this.isLoader = false;
        } else {
          this.notFound = true;
          this.isLoader = false;
        }
      },
      error: (err) => {},
    });
  }

  public initForm() {
    this.formPacket = new FormGroup<IFormGroup>({
      currentPacket: new FormControl(),
      countAdverts: new FormControl(),
      zone: new FormControl(),
      currentZone: new FormControl(),
    });
  }

  packetBase(selectedPackage: any) {
    this.selectedBase = selectedPackage;
  }

  packetPremium(selectedPackage: any) {
    this.selectedPremium = selectedPackage;
  }

  packetMega(selectedPackage: any) {
    this.selectedMega = selectedPackage;
  }

  closeBar() {
    this.close.emit();
  }

  choosePacket(e: any, packet: any) {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.buy_package',
    });
    dialogRef.subscribe((result) => {
      if (result) {
        const v = this.formPacket.getRawValue();
        if (!this.planned_at_premium && !this.planned_at_mega && !this.planned_at_base) {
          const value = {
            payment_method: 'account',
            category_id: v.countAdverts.category_id,
            size: v.countAdverts.size,
            type: v.countAdverts.type,
            zone_id: v.countAdverts.zone_id,
            is_premium: v.countAdverts.is_premium,
          };
          this.olxAdvertService.buyPacket(value, this.channel_id).subscribe({
            next: (res) => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_bought,
              );
            },
            error: (res) => {
              (res.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code]);
                this.close.emit();
              });
            },
          });
        } else {
          if (this.planned_at_base && packet.type === 'base' && packet.is_premium === false) {
            this.planned_at = this.planned_at_base;
          } else if (this.planned_at_premium && packet.type === 'base' && packet.is_premium === true) {
            this.planned_at = this.planned_at_premium;
          } else if (this.planned_at_mega && packet.type === 'mega') {
            this.planned_at = this.planned_at_mega;
          }
          const value = {
            name: this.calendar?.name || '',
            planned_at: this.planned_at,
            type: 'PACKET',
            status: this.isDraft,
            x_data: {
              channel_id: this.channel_id,
              payment_method: 'account',
              category_id: v.countAdverts.category_id,
              size: v.countAdverts.size,
              type: v.countAdverts.type,
              zone_id: v.countAdverts.zone_id,
              is_premium: v.countAdverts.is_premium,
            },
          };
          this.olxAdvertService.createPlanner(value).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_planned,
              );
              this.close.emit();
            },
            error: () => {
              this.notificationService.showError(
                '',
                activeTranslations.notifications_texts.error.error_while_planner,
              );
              this.close.emit();
            },
          });
        }
      }
    });
  }

  getZones() {
    this.olxAdvertService.getZones(this.current_category_id, this.channel_id).subscribe((res) => {
      this.zones = res.map((v: any) => {
        return { ...v, label_ua: v.labels.uk };
      });
      if (!this.zones.length) this.initPackets();
    });
  }

  selectZone(e: any) {
    this.isCurrentZone = true;
    const v = this.formPacket.get('zone')?.value;
    this.currentZone = v;
    if (this.currentZone.sub_zones.length === 0) {
      this.isCurrentZone = false;
      this.initPackets(v.id);
    } else {
      this.currentZone = this.currentZone.sub_zones.map((v: any) => {
        return { ...v, label_ua: v.labels.uk };
      });
    }
  }

  selectCurrentZone(e: any) {
    const v = this.formPacket.get('currentZone')?.value;
    this.initPackets(v.id);
  }

  openPlanner(packet: any) {
    this.modalService
      .openModal({
        title: 'planner_paid',
        translate: true,
        component: PlannerComponent,
        data: {
          modalType: MODAL_TYPES.PACKET,
        },
      })
      .subscribe((res) => {
        if (res) {
          if (packet.type === 'base' && packet.is_premium === false) {
            const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
            const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
            this.calendar = res;
            this.planned_at_base = dateTime;
            this.isDraft = res.x_data.status;
          } else if (packet.type === 'base' && packet.is_premium) {
            const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
            const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
            this.calendar = res;
            this.planned_at_premium = dateTime;
            this.isDraft = res.x_data.status;
          } else if (packet.type === 'mega') {
            const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
            const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
            this.calendar = res;
            this.planned_at_mega = dateTime;
            this.isDraft = res.x_data.status;
          }
        }
      });
  }

  cancelPlanner(packet?: any) {
    if (packet.type === 'base' && packet.is_premium === false) {
      this.planned_at_base = null;
    } else if (packet.type === 'base' && packet.is_premium === true) {
      this.planned_at_premium = null;
    } else if (packet.type === 'mega') {
      this.planned_at_mega = null;
    }
  }
}
